<script setup lang="ts">
  import { computed, useId, useSlots } from 'vue';
  import type {
    PropertyListProps,
    PropertyListSlots,
  } from './PropertyList.types';
  import { PropertyListVariant } from './PropertyList.types';
  import {
    actionsClass,
    bodyClass,
    descriptionClass,
    descriptionSlotWrapperClass,
    headerClass,
    headerEndClass,
    headerMainClass,
    rootClass,
    rootVariantClass,
    titleClass,
    trailingClass,
  } from './PropertyList.css';

  const props = withDefaults(defineProps<PropertyListProps>(), {
    variant: PropertyListVariant.Standalone,
  });

  const rootClasses = computed(() => [
    rootClass,
    rootVariantClass[props.variant],
  ]);

  defineSlots<PropertyListSlots>();

  const uid = useId();
  const resolvedTestId = computed(
    () => props.testId ?? `ds-property-list-${uid}`,
  );

  const slots = useSlots();
  const hasTitleSlot = computed(() => !!slots.title);
  const hasDescriptionSlot = computed(() => !!slots.description);
  const hasActions = computed(() => !!slots.actions);
  const hasTrailing = computed(() => !!slots.trailing);

  const sectionAriaLabel = computed(() =>
    hasTitleSlot.value ? props.title : undefined,
  );
  const sectionAriaLabelledby = computed(() =>
    hasTitleSlot.value ? undefined : `${resolvedTestId.value}-title`,
  );
  const sectionAriaDescribedby = computed(() => {
    if (hasDescriptionSlot.value) {
      return `${resolvedTestId.value}-description-slot`;
    }
    if (props.description) {
      return `${resolvedTestId.value}-description`;
    }
    return undefined;
  });
</script>

<template>
  <section
    :class="rootClasses"
    :data-testid="resolvedTestId"
    :aria-label="sectionAriaLabel"
    :aria-labelledby="sectionAriaLabelledby"
    :aria-describedby="sectionAriaDescribedby"
  >
    <div :class="headerClass" :data-testid="`${resolvedTestId}-header`">
      <div :class="headerMainClass">
        <div v-if="hasTitleSlot" :data-testid="`${resolvedTestId}-title-slot`">
          <slot name="title" />
        </div>
        <h2
          v-else
          :id="`${resolvedTestId}-title`"
          :class="titleClass"
          :data-testid="`${resolvedTestId}-title-heading`"
        >
          {{ title }}
        </h2>
        <div
          v-if="hasDescriptionSlot"
          :id="`${resolvedTestId}-description-slot`"
          :class="descriptionSlotWrapperClass"
          :data-testid="`${resolvedTestId}-description-slot`"
        >
          <slot name="description" />
        </div>
        <p
          v-else-if="description"
          :id="`${resolvedTestId}-description`"
          :class="descriptionClass"
          :data-testid="`${resolvedTestId}-description`"
        >
          {{ description }}
        </p>
      </div>
      <div v-if="hasActions || hasTrailing" :class="headerEndClass">
        <div
          v-if="hasActions"
          :class="actionsClass"
          :data-testid="`${resolvedTestId}-actions`"
        >
          <slot name="actions" />
        </div>
        <div
          v-if="hasTrailing"
          :class="trailingClass"
          :data-testid="`${resolvedTestId}-trailing`"
        >
          <slot name="trailing" />
        </div>
      </div>
    </div>
    <div :class="bodyClass"><slot /></div>
  </section>
</template>
